/**
 * The mission and vision icons, as inline SVG.
 *
 * **Why these two and not the ones that were here.** The pair in `assets/media/` is a
 * concentric-circle dartboard with an arrow through it and — the reason they are being
 * replaced — a wide open eye with rays coming off it. On a Christian foundation's home
 * page, next to a heading that says "Our Vision", a rayed eye reads as something the
 * Foundation is not, and it is the first thing anyone notices about that section.
 *
 * The replacements are heraldic rather than diagrammatic:
 *
 *   Our Mission → a crown surmounted by a cross. King of Kings, and a mission is
 *                 something given by a sovereign, not something drawn on a target.
 *   Our Vision  → a fleur-de-lis. The classic royal charge, and a lily; noble,
 *                 old-fashioned, and unambiguous.
 *
 * **Inline SVG, not `<img src>`.** The files in `assets/media/` bake a fixed ink into every
 * path — `#1A212D`, which was the Foundation's `--ul-black` when they were drawn and is not
 * anybody's as of 2026-09-19 — so they cannot follow a theme and would be a hard-coded colour
 * in any app that reused them (ground rule 4). Inline, they are painted with tokens and the
 * palette comes through. This follows the pattern `data/features.tsx` established.
 *
 * Edit these in a vector editor and paste the result; do not adjust coordinates by hand.
 */
import type { ReactElement } from 'react'

/** A heraldic crown, surmounted by a cross. */
export const CROWN_ICON: ReactElement = (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* the cross above the centre peak */}
    <path d="M17.15 2.4h1.7v6h-1.7z" fill="var(--ul-c3)" />
    <path d="M15.2 4.35h5.6v1.7h-5.6z" fill="var(--ul-c3)" />
    {/* the body of the crown: two outer peaks, the centre peak, two valleys */}
    <path
      d="M3.6 26.1V11.4a.85.85 0 0 1 1.5-.55l6.1 6.6L17.2 8.4a.95.95 0 0 1 1.6 0l6 9.05 6.1-6.6a.85.85 0 0 1 1.5.55v14.7z"
      fill="var(--ul-secondary)"
    />
    {/* orbs on the two outer peaks */}
    <circle cx="3.9" cy="9.2" r="2.3" fill="var(--ul-c3)" />
    <circle cx="32.1" cy="9.2" r="2.3" fill="var(--ul-c3)" />
    {/* the band */}
    <rect x="2.4" y="27.6" width="31.2" height="5.4" rx="2.2" fill="var(--ul-secondary)" />
  </svg>
)

/** A fleur-de-lis. */
export const FLEUR_ICON: ReactElement = (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* the centre petal */}
    <path
      d="M18 2.2c2.9 4.9 4.3 9 4.3 12.1 0 2.4-1.4 4.5-4.3 6.5-2.9-2-4.3-4.1-4.3-6.5 0-3.1 1.4-7.2 4.3-12.1z"
      fill="var(--ul-secondary)"
    />
    {/* the two side petals, curling outward and back */}
    <path
      d="M16.4 12.6c.5 3.9.3 6.7-.7 8.2-1.4 2.2-4 2.7-6.1 1.1-2.3-1.7-2.9-4.9-1.3-7.4 1.4-2.2 4.2-3 8.1-1.9z"
      fill="var(--ul-secondary)"
    />
    <path
      d="M19.6 12.6c-.5 3.9-.3 6.7.7 8.2 1.4 2.2 4 2.7 6.1 1.1 2.3-1.7 2.9-4.9 1.3-7.4-1.4-2.2-4.2-3-8.1-1.9z"
      fill="var(--ul-secondary)"
    />
    {/* the band that gathers the three petals */}
    <rect x="11.6" y="21.1" width="12.8" height="3.4" rx="1.4" fill="var(--ul-c3)" />
    {/* the foot */}
    <path
      d="M18 25.3c2.1 3 3.1 5.4 3.1 7.1 0 .5-.1 1-.3 1.4h-5.6a3.6 3.6 0 0 1-.3-1.4c0-1.7 1-4.1 3.1-7.1z"
      fill="var(--ul-secondary)"
    />
  </svg>
)
